import React, { useState } from 'react';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { submitContactForm } from '../../util/api';
import css from './BecomeSellerPage.module.css';

const ADVANTAGES = [
  {
    icon: '💰',
    title: 'Commission attractive',
    text: 'Profitez de frais réduits et d\'une tarification transparente pour maximiser vos marges.',
  },
  {
    icon: '👁️',
    title: 'Visibilité immédiate',
    text: 'Accédez à une audience qualifiée passionnée de domotique et de maison connectée.',
  },
  {
    icon: '🛠️',
    title: 'Outils de gestion',
    text: 'Gérez votre catalogue, vos commandes et votre facturation depuis un tableau de bord intuitif.',
  },
  {
    icon: '🤝',
    title: 'Accompagnement dédié',
    text: 'Notre équipe vous guide à chaque étape, de la mise en ligne à la première vente.',
  },
];

const BecomeSellerPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [productType, setProductType] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const onSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatus(null);

    const formMessage = [
      company ? `Entreprise : ${company}` : null,
      `Type de produits : ${productType}`,
      '',
      message,
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await submitContactForm({
        name,
        email,
        subject: 'Candidature vendeur',
        message: formMessage,
      });

      setStatus({ type: 'success', message: 'Merci ! Votre candidature a bien été envoyée. Nous reviendrons vers vous rapidement.' });
      setName('');
      setEmail('');
      setCompany('');
      setProductType('');
      setMessage('');
    } catch (err) {
      setStatus({ type: 'error', message: 'Désolé, une erreur s\'est produite. Veuillez réessayer.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page
      title="Devenir vendeur | Neokasa"
      description="Rejoignez la marketplace Neokasa et vendez vos produits domotique et maison connectée à une audience qualifiée."
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {/* Hero */}
          <section className={css.hero}>
            <div className={css.container}>
              <h1 className={css.heroTitle}>Devenez vendeur sur Neokasa</h1>
              <p className={css.heroSubtitle}>
                Rejoignez la première marketplace française dédiée à la domotique et à la maison connectée.
                Touchez des milliers d'acheteurs passionnés et développez votre activité en toute simplicité.
              </p>
            </div>
          </section>

          {/* Avantages */}
          <section className={css.advantagesSection}>
            <div className={css.container}>
              <h2 className={css.sectionTitle}>Pourquoi vendre sur Neokasa ?</h2>
              <div className={css.advantagesGrid}>
                {ADVANTAGES.map(({ icon, title, text }) => (
                  <div key={title} className={css.advantageCard}>
                    <span className={css.advantageIcon}>{icon}</span>
                    <h3 className={css.advantageTitle}>{title}</h3>
                    <p className={css.advantageText}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Formulaire */}
          <section className={css.formSection}>
            <div className={css.container}>
              <h2 className={css.sectionTitle}>Candidature vendeur</h2>
              <p className={css.sectionSubtitle}>
                Remplissez le formulaire ci-dessous et notre équipe vous recontactera sous 48h.
              </p>

              <form className={css.form} onSubmit={onSubmit} noValidate>
                <div className={css.fieldGrid}>
                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="seller-name">Nom *</label>
                    <input
                      id="seller-name"
                      className={css.input}
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="seller-email">Email *</label>
                    <input
                      id="seller-email"
                      className={css.input}
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="vous@exemple.com"
                    />
                  </div>
                </div>

                <div className={css.fieldGrid}>
                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="seller-company">Nom de l'entreprise</label>
                    <input
                      id="seller-company"
                      className={css.input}
                      type="text"
                      value={company}
                      onChange={e => setCompany(e.target.value)}
                      placeholder="Optionnel"
                    />
                  </div>

                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="seller-product-type">Type de produits *</label>
                    <input
                      id="seller-product-type"
                      className={css.input}
                      type="text"
                      value={productType}
                      onChange={e => setProductType(e.target.value)}
                      required
                      placeholder="Ex : éclairage connecté, thermostats…"
                    />
                  </div>
                </div>

                <div className={css.fieldRow}>
                  <label className={css.label} htmlFor="seller-message">Présentez-vous *</label>
                  <textarea
                    id="seller-message"
                    className={css.textarea}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    required
                    placeholder="Parlez-nous de votre activité, vos produits, vos motivations…"
                    rows={6}
                  />
                </div>

                {status ? (
                  <div className={status.type === 'success' ? css.success : css.error}>
                    {status.message}
                  </div>
                ) : null}

                <button className={css.submit} type="submit" disabled={submitting}>
                  {submitting ? 'Envoi en cours…' : 'Envoyer ma candidature'}
                </button>
              </form>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BecomeSellerPage;
